// This module will be used to verify that each row in the parent table field is accounted for much like it does in
// verifyFormGroupFields, but it will be specific to the table field and its rows.
// This is necessary because there could be a new field added to the table and that needs to be setup correctly

import { bulkSaveFieldInstances, createFieldForBatchResolver } from "db/queries/fieldInstanceQueries";
import { FieldInstance, FieldInstanceForResolver, FieldTemplate, Image } from "../db/definitions";
import { recordRepairBreadcrumb, recordSentry } from "./recordSentry";
import { v4 as uuid } from "uuid";
import { bulkSaveImageDocs } from "db/queries/imageQueries";
import { handleBatchCrud } from "./handleBatchCrud";
import batchCreateFieldInstances from "./batchCreateFieldInstances";
import batchCreateImages from "./batchCreateImages";
import updateTable from "./updateTable";

async function verifyTableFields(
	tableFieldTemplate: FieldTemplate,
	tableFieldInstances: FieldInstance[],
	parentFieldInstance: FieldInstance,
	online: boolean,
	projectId: string,
): Promise<FieldInstance> {
	const rowOrder = tableFieldTemplate.rowOrder || [];
	const cellFieldTemplates = tableFieldTemplate.tableFields?.items || [];
	const rowIdsInTable = parentFieldInstance.fieldsOrder || [];

	// Safety: if rowOrder is empty the template wasn't fully loaded — proceeding would wipe all rows.
	if (rowOrder.length === 0) {
		return parentFieldInstance;
	}
	const fieldInstancesById: Record<string, FieldInstance> = {};
	const fieldTemplatesById: Record<string, FieldTemplate> = {};

	for (const cellFieldTemplate of cellFieldTemplates) {
		if (cellFieldTemplate.id) {
			fieldTemplatesById[cellFieldTemplate.id] = cellFieldTemplate;
		}
	}

	for (const fieldInstance of tableFieldInstances) {
		if (fieldInstance.id) {
			fieldInstancesById[fieldInstance.id] = fieldInstance;
		}
	}

	let needToUpdateParent = false;

	const fieldsToCreate: FieldInstance[] = [];
	const newFieldsOrderArray: string[] = [];
	const imageDocsToCreate: Image[] = [];

	// Build a set of all cell IDs currently referenced in fieldsOrder so we can
	// identify orphaned cells (e.g. hidden cells dropped by a pre-fix sort bug).
	const allIdsInFieldsOrder = new Set<string>();
	for (const rowIds of rowIdsInTable) {
		for (const id of rowIds.split(",")) {
			if (id) allIdsInFieldsOrder.add(id);
		}
	}

	// Map orphaned cells (exist in DB with correct tableField but absent from
	// fieldsOrder) by their template ID so they can be re-linked instead of
	// replaced with new empty cells. Kept as a queue per template: each orphan can
	// only be handed to one row, otherwise two rows would share a cell and an edit
	// in one would show up in the other.
	const orphanedFieldsByTemplate: Record<string, FieldInstance[]> = {};
	for (const fieldInstance of tableFieldInstances) {
		if (fieldInstance.id && !allIdsInFieldsOrder.has(fieldInstance.id) && fieldInstance.fieldTemplateId) {
			(orphanedFieldsByTemplate[fieldInstance.fieldTemplateId] ||= []).push(fieldInstance);
		}
	}
	// Orphans with content are handed out before empty ones.
	for (const orphans of Object.values(orphanedFieldsByTemplate)) {
		orphans.sort((a, b) => {
			const aHasContent = !!a.value || !!(a.imageId && a.image?.key);
			const bHasContent = !!b.value || !!(b.imageId && b.image?.key);
			if (aHasContent !== bHasContent) return aHasContent ? -1 : 1;
			return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
		});
	}

	for (let rowIdx = 0; rowIdx < rowIdsInTable.length; rowIdx++) {
		const rowIds = rowIdsInTable[rowIdx];
		const existingFieldIds = rowIds.split(",");
		const newRowOrderArray: string[] = [];

		// Create a map of existing field instances by their template ID.
		// When a row has duplicate cells for the same column (accumulation bug),
		// keep the best one: real image key > any value > most-recent wins.
		const existingFieldsByTemplate: Record<string, FieldInstance> = {};
		for (const fieldId of existingFieldIds) {
			const fieldInstance = fieldInstancesById[fieldId];
			if (fieldInstance && fieldInstance.fieldTemplateId) {
				const templateId = fieldInstance.fieldTemplateId;
				const existing = existingFieldsByTemplate[templateId];
				if (!existing) {
					existingFieldsByTemplate[templateId] = fieldInstance;
				} else {
					const newHasRealImage = !!(fieldInstance.imageId && fieldInstance.image?.key);
					const existingHasRealImage = !!(existing.imageId && existing.image?.key);
					const newHasValue = !!fieldInstance.value || !!fieldInstance.imageId;
					const existingHasValue = !!existing.value || !!existing.imageId;
					const newUpdatedAt = fieldInstance.updatedAt ?? "";
					const existingUpdatedAt = existing.updatedAt ?? "";

					let shouldReplace = false;
					if (!existingHasValue && newHasValue) {
						shouldReplace = true;
					} else if (existingHasValue === newHasValue) {
						// Prefer cell with a real uploaded image over one with empty imageId
						if (!existingHasRealImage && newHasRealImage) {
							shouldReplace = true;
						} else if (existingHasRealImage === newHasRealImage) {
							shouldReplace = newUpdatedAt > existingUpdatedAt;
						}
					}
					if (shouldReplace) {
						existingFieldsByTemplate[templateId] = fieldInstance;
					}
				}
			}
		}
		// Over-long rows need their corrected fieldsOrder persisted
		if (existingFieldIds.length > rowOrder.length) {
			needToUpdateParent = true;
		}

		// For each template in the row order, use existing field or create new one
		for (const expectedTemplateId of rowOrder) {
			// First check cells currently in fieldsOrder, then fall back to orphaned
			// cells (e.g. hidden cells dropped from fieldsOrder by a pre-fix sort).
			const existingField = existingFieldsByTemplate[expectedTemplateId] ?? orphanedFieldsByTemplate[expectedTemplateId]?.shift();

			if (existingField) {
				newRowOrderArray.push(existingField.id);
				// If we recovered an orphan, the parent fieldsOrder needs updating
				if (!existingFieldsByTemplate[expectedTemplateId]) {
					needToUpdateParent = true;
				}
			} else {
				// Need to create a new field instance for this template
				needToUpdateParent = true;
				const cellFieldTemplate = fieldTemplatesById[expectedTemplateId];
				if (!cellFieldTemplate) {
					recordSentry({
						error: new Error(`Field template with id ${expectedTemplateId} is missing for table field ${tableFieldTemplate.id}`),
						msg: `Error verifying table fields for table field ${tableFieldTemplate.id}`,
					});
					continue;
				}
				const newId = uuid();
				const timestamp = new Date().toISOString();
				const newFieldInstance: FieldInstance = {
					id: newId,
					formGroup: parentFieldInstance.formGroup,
					fieldTemplateId: expectedTemplateId,
					value: "",
					tableField: parentFieldInstance.id,
					fieldsOrder: [],
					notes: "",
					forceUpdateData: "",
					exportInstruction: "",
					authors: [],
					editing: "",
					lastAuthor: "",
					tableFields: {
						items: [],
					},
					group: parentFieldInstance.group,
					createdAt: timestamp,
					updatedAt: timestamp,
				};

				// check if it needs an image too now
				if (cellFieldTemplate.inputType === "image") {
					const imageId = uuid();
					const timestamp = new Date().toISOString();
					const imageDoc: Image = {
						id: imageId,
						formGroup: newFieldInstance.formGroup,
						project: projectId,
						key: "",
						rotation: "",
						dpi: "",
						aspectX: "",
						aspectY: "",
						group: newFieldInstance.group,
						createdAt: timestamp,
						updatedAt: timestamp,
					};
					newFieldInstance.imageId = imageDoc.id;
					imageDocsToCreate.push(imageDoc);
				}

				newRowOrderArray.push(newFieldInstance.id);
				fieldsToCreate.push(newFieldInstance);
			}
		}
		const rowOrderString = newRowOrderArray.join(",");
		newFieldsOrderArray.push(rowOrderString);
	}

	const newParent: FieldInstance = {
		...parentFieldInstance,
		fieldsOrder: newFieldsOrderArray,
		tableFields: {
			items: [...tableFieldInstances, ...fieldsToCreate],
		},
	};

	if (needToUpdateParent) {
		recordRepairBreadcrumb("verifyTableFields", {
			parentId: parentFieldInstance.id,
			formGroup: parentFieldInstance.formGroup,
			fieldTemplateId: tableFieldTemplate.id,
			online,
			rowsIn: rowIdsInTable.length,
			rowsOut: newFieldsOrderArray.length,
			cellsCreated: fieldsToCreate.length,
			imageDocsCreated: imageDocsToCreate.length,
		});

		// Create the new cells (and their image docs) before pointing the parent's fieldsOrder at them. If creation fails
		// the parent is left untouched and the next load repairs again; the reverse order could leave fieldsOrder
		// referencing cells that don't exist.
		const fieldInstancesForResolver: FieldInstanceForResolver[] = fieldsToCreate.map((fieldInstance) => createFieldForBatchResolver(fieldInstance));
		try {
			if (online) {
				await handleBatchCrud(fieldInstancesForResolver, batchCreateFieldInstances, 25);
				await handleBatchCrud(imageDocsToCreate, batchCreateImages, 25);
			} else {
				await bulkSaveFieldInstances(fieldInstancesForResolver);
				await bulkSaveImageDocs(imageDocsToCreate);
			}
		} catch (e) {
			recordSentry({
				error: e instanceof Error ? e : new Error(String(e)),
				msg: `verifyTableFields could not create cells for parent ${parentFieldInstance.id}; fieldsOrder left unchanged`,
			});
			return parentFieldInstance;
		}

		try {
			await updateTable(newParent, online);
		} catch (e) {
			recordSentry({
				error: e instanceof Error ? e : new Error(String(e)),
				msg: `verifyTableFields updateTable failed for parent ${parentFieldInstance.id}`,
			});
		}
	}

	return newParent;
}

export default verifyTableFields;
